import * as React from "react";
import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Form, Formik } from "formik";
import { Button, Col, Modal, Row } from "components/bootstrap";
import FormikInput from "components/common/FormikInput";
import Spinner from "components/common/Spinner";
import { ModalSubmit, IfPermitted } from "components/common";
import { ConfigurationsActions, ConfigurationsStore } from "stores/configurations/ConfigurationsStore";
import { ConfigurationType } from "components/configurations/ConfigurationTypes";
import { getConfig } from "components/configurations/helpers";
import { useStore } from "stores/connect";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useLocation from "routing/useLocation";
import { getPathnameWithoutId } from "util/URLUtils";
import { DEFAULT_PASSWORD_COMPLEXITY_CONFIG, PASSWORD_SPECIAL_CHARACTERS } from "logic/users/passwordComplexity";
const StyledDefList = styled.dl.attrs({ className: "deflist" })(
  ({ theme }) => css`
    &&.deflist {
      dd {
        padding-left: ${theme.spacings.md};
        margin-left: 300px;
      }
    }
  `
);
const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `
);
const configType = ConfigurationType.PASSWORD_COMPLEXITY_CONFIG;
const PasswordComplexityConfig = () => {
  const [showModal, setShowModal] = useState(false);
  const [viewConfig, setViewConfig] = useState(void 0);
  const [formConfig, setFormConfig] = useState(void 0);
  const configuration = useStore(ConfigurationsStore, (state) => state == null ? void 0 : state.configuration);
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  useEffect(() => {
    ConfigurationsActions.listPasswordComplexityConfig(configType).then(() => {
      var _a;
      const config = (_a = getConfig(configType, configuration)) != null ? _a : DEFAULT_PASSWORD_COMPLEXITY_CONFIG;
      setViewConfig(config);
      setFormConfig(config);
    });
  }, [configuration]);
  const saveConfig = (values) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.CONFIGURATIONS.PASSWORD_COMPLEXITY_UPDATED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "password-complexity",
      app_action_value: "configuration-save"
    });
    const payload = { ...values, min_length: Number(values.min_length) };
    ConfigurationsActions.update(configType, payload).then(() => {
      setShowModal(false);
    });
  };
  const resetConfig = () => {
    setShowModal(false);
    setFormConfig(viewConfig);
  };
  const validate = (values) => {
    const errors = {};
    if (!values.min_length || Number(values.min_length) < 1) {
      errors.min_length = "Minimum length must be at least 1";
    }
    return errors;
  };
  const renderRequirement = (label, value) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("dt", null, label), /* @__PURE__ */ React.createElement("dd", { "aria-label": label }, value));
  const requirementLabel = (required) => required ? "Required" : "Not required";
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Password Complexity"), /* @__PURE__ */ React.createElement("p", null, "Configure the minimum password length and character requirements enforced for local users."), !viewConfig ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledDefList, null, renderRequirement("Minimum length:", viewConfig.min_length), renderRequirement("Require uppercase letters:", requirementLabel(viewConfig.require_uppercase)), renderRequirement("Require lowercase letters:", requirementLabel(viewConfig.require_lowercase)), renderRequirement("Require numbers:", requirementLabel(viewConfig.require_numbers)), renderRequirement("Require special characters:", requirementLabel(viewConfig.require_special_chars))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "info",
      onClick: () => {
        setShowModal(true);
      }
    },
    "Edit configuration"
  )), /* @__PURE__ */ React.createElement(Modal, { show: showModal && !!formConfig, onHide: resetConfig }, /* @__PURE__ */ React.createElement(Formik, { onSubmit: saveConfig, initialValues: formConfig, validate }, ({ isSubmitting }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Update Password Complexity")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "number",
      name: "min_length",
      id: "min_length",
      label: "Minimum length",
      min: 1,
      required: true,
      help: "Passwords shorter than this length will be rejected."
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_uppercase",
      id: "require_uppercase",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Require uppercase letters"),
      help: "Password must include at least one uppercase character (A-Z)."
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_lowercase",
      id: "require_lowercase",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Require lowercase letters"),
      help: "Password must include at least one lowercase character (a-z)."
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_numbers",
      id: "require_numbers",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Require numbers"),
      help: "Password must include at least one numeric character (0-9)."
    }
  )), /* @__PURE__ */ React.createElement(Col, { sm: 12 }, /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      type: "checkbox",
      name: "require_special_chars",
      id: "require_special_chars",
      label: /* @__PURE__ */ React.createElement(LabelSpan, null, "Require special characters"),
      help: /* @__PURE__ */ React.createElement(React.Fragment, null, "Password must include at least one of the following characters:", /* @__PURE__ */ React.createElement("br", null), PASSWORD_SPECIAL_CHARACTERS)
    }
  )))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      onCancel: resetConfig,
      isSubmitting,
      isAsyncSubmit: true,
      submitLoadingText: "Update configuration",
      submitButtonText: "Update configuration"
    }
  )))))));
};
export default PasswordComplexityConfig;
